import React from "react";
import { Info, ArrowUp, ArrowDown } from "lucide-react";

// Shared presentational primitives for the Admin Analytics dashboard.
// Authored in Tailwind against AI Tutor's brand palette. The reference
// prototype's CSS-class hover system (.evt-tip / .evt-pop / .evt-row) is
// reimplemented here with Tailwind `group` / `group-hover` utilities.

export function Card({
  children,
  className = "",
  hover = true,
}: {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
}) {
  return (
    <div
      className={`rounded-[11px] border border-[#DED8CC] bg-[#FBF8F1] p-6 transition-[border-color,box-shadow] duration-150 ${
        hover
          ? "hover:border-[#28543D]/40 hover:shadow-[0_8px_22px_rgba(23,63,41,0.08)]"
          : ""
      } ${className}`}
    >
      {children}
    </div>
  );
}

// Hover tooltip. Wraps `children`; reveals `label` bubble above on hover/focus.
export function Tip({
  label,
  children,
  align = "center",
  className = "",
  style,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  align?: "center" | "right" | "left";
  className?: string;
  style?: React.CSSProperties;
}) {
  const alignClass =
    align === "right"
      ? "right-0"
      : align === "left"
        ? "left-0"
        : "left-1/2 -translate-x-1/2 group-hover/tip:-translate-x-1/2 group-focus-within/tip:-translate-x-1/2";
  return (
    <span
      className={`group/tip relative inline-flex ${className}`}
      style={style}
      tabIndex={0}
    >
      {children}
      <span
        className={`pointer-events-none invisible absolute bottom-[calc(100%+10px)] z-[1000] w-max max-w-[280px] translate-y-1 rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] px-3 py-2.5 text-left text-[12.5px] font-medium leading-[1.55] text-[#294B39] opacity-0 shadow-[0_10px_34px_rgba(18,18,18,0.16)] transition-[opacity,transform] duration-150 group-hover/tip:visible group-hover/tip:translate-y-0 group-hover/tip:opacity-100 group-focus-within/tip:visible group-focus-within/tip:translate-y-0 group-focus-within/tip:opacity-100 ${alignClass}`}
      >
        {label}
      </span>
    </span>
  );
}

export function InfoIcon({
  text,
  align = "center",
}: {
  text: string;
  align?: "center" | "right";
}) {
  return (
    <Tip label={text} align={align}>
      <Info
        size={15}
        strokeWidth={2}
        className="block cursor-help text-[#687368]"
      />
    </Tip>
  );
}

// Popover contents: a labelled key/value list.
function PopList({
  title,
  items,
}: {
  title: string;
  items: { label: string; value: React.ReactNode }[];
}) {
  return (
    <>
      <div className="mb-2 text-[11.5px] font-bold uppercase tracking-[0.04em] text-[#687368]">
        {title}
      </div>
      <div className="flex flex-col gap-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <span className="truncate text-[13px] text-[#687368]">
              {it.label}
            </span>
            <span className="shrink-0 text-[13px] font-bold text-[#294B39]">
              {it.value}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// One-level-deeper breakdown shown on row hover. Place inside a `group/row`.
export function RowPop({
  title,
  items,
  side = "right",
}: {
  title: string;
  items: { label: string; value: React.ReactNode }[];
  side?: "left" | "right";
}) {
  return (
    <div
      className={`pointer-events-none invisible absolute top-[calc(100%-6px)] z-[46] w-[244px] -translate-y-1.5 rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] px-3.5 py-3 opacity-0 shadow-[0_14px_40px_rgba(18,18,18,0.18)] transition-[opacity,transform] duration-150 group-hover/row:visible group-hover/row:translate-y-0 group-hover/row:opacity-100 group-focus-within/row:visible group-focus-within/row:translate-y-0 group-focus-within/row:opacity-100 ${
        side === "left" ? "left-0" : "right-0"
      }`}
    >
      <PopList title={title} items={items} />
    </div>
  );
}

export function SectionTitle({ title, tag }: { title: string; tag?: string }) {
  return (
    <div className="flex items-center gap-3">
      <h2 className="m-0 text-[22px] font-bold tracking-[-0.01em] text-[#294B39]">
        {title}
      </h2>
      {tag && (
        <span className="rounded-[4px] border border-[#28543D]/25 bg-[#28543D]/5 px-3 py-1 text-[12.5px] font-semibold text-[#28543D]">
          {tag}
        </span>
      )}
    </div>
  );
}

export function MetricTitle({
  children,
  info,
  align,
}: {
  children: React.ReactNode;
  info?: string;
  align?: "center" | "right";
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[13px] font-bold text-[#687368]">{children}</span>
      {info && <InfoIcon text={info} align={align} />}
    </div>
  );
}

export function BigNumber({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`font-libre-baskerville font-normal leading-[1.05] text-[#294B39] ${className}`}
    >
      {children}
    </div>
  );
}

// delta pill: green when positive, peach when negative
export function DeltaChip({
  delta,
  suffix = "pts",
  compareLabel = "vs last month",
}: {
  delta: number;
  suffix?: string;
  compareLabel?: string;
}) {
  const positive = delta >= 0;
  return (
    <span
      className={`inline-flex w-fit items-center gap-1 rounded-[4px] px-2.5 py-[3px] text-xs font-bold ${
        positive
          ? "bg-[#28543D]/5 text-[#28543D]"
          : "bg-[#FBF0DC] text-[#8A5A1C]"
      }`}
    >
      {positive ? (
        <ArrowUp size={13} strokeWidth={2.5} />
      ) : (
        <ArrowDown size={13} strokeWidth={2.5} />
      )}
      {positive ? "+" : ""}
      {delta}
      {suffix === "%" ? "%" : ` ${suffix}`} {compareLabel}
    </span>
  );
}

// Metric card with an optional hover breakdown popover (Engagement section).
export function StatCard({
  title,
  info,
  value,
  secondary,
  chip,
  breakdowns,
  popSide = "right",
}: {
  title: string;
  info: string;
  value: React.ReactNode;
  secondary: string;
  chip?: React.ReactNode;
  breakdowns?: { title: string; items: { label: string; value: string }[] }[];
  popSide?: "left" | "right";
}) {
  const hasPop = breakdowns && breakdowns.some((b) => b.items.length > 0);
  return (
    <div className="group/pop relative">
      <Card className="flex flex-col gap-3">
        <MetricTitle info={info} align="center">
          {title}
        </MetricTitle>
        <BigNumber className="text-[34px]">{value}</BigNumber>
        <div className="flex flex-col gap-2">
          <span className="text-[13px] text-[#687368]">{secondary}</span>
          {chip}
        </div>
      </Card>
      {hasPop && (
        <div
          className={`pointer-events-none invisible absolute top-0 z-[45] w-[230px] rounded-[11px] border border-[#DED8CC] bg-[#FBF8F1] px-4 py-3.5 opacity-0 shadow-[0_14px_40px_rgba(18,18,18,0.16)] transition-[opacity,transform] duration-150 group-hover/pop:visible group-hover/pop:translate-x-0 group-hover/pop:opacity-100 ${
            popSide === "left"
              ? "right-[calc(100%+12px)] translate-x-1.5"
              : "left-[calc(100%+12px)] -translate-x-1.5"
          }`}
        >
          <div className="flex flex-col gap-3.5">
            {breakdowns!.map((group) => (
              <PopList
                key={group.title}
                title={group.title}
                items={group.items}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact donut gauge for a single percentage (SVG, no chart lib).
export function Donut({
  value,
  size = 74,
  color = "#007047",
  trackColor = "#F0FBF6",
  label,
  sublabel,
}: {
  value: number;
  size?: number;
  color?: string;
  trackColor?: string;
  label?: React.ReactNode;
  sublabel?: string;
}) {
  const v = Math.max(0, Math.min(100, value));
  const stroke = size * 0.15;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="block -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-[1.1]">
        <span className="text-[15px] font-bold text-[#294B39]">
          {label ?? `${Math.round(v)}%`}
        </span>
        {sublabel && (
          <span className="text-[10px] text-[#687368]">{sublabel}</span>
        )}
      </div>
    </div>
  );
}

// small inline sparkline
export function Sparkline({
  data,
  color = "#1C9C6D",
}: {
  data: number[];
  color?: string;
}) {
  const w = 96;
  const h = 28;
  const pad = 2;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((val, i) => {
      const x = pad + (i * (w - pad * 2)) / (data.length - 1);
      const y = h - pad - ((val - min) / range) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const lastX = w - pad;
  const lastY =
    h - pad - ((data[data.length - 1] - min) / range) * (h - pad * 2);
  return (
    <svg width={w} height={h} className="block">
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
    </svg>
  );
}

// shimmer skeleton block
export function Skel({
  w,
  h = 14,
  r = 8,
  className = "",
}: {
  w?: number | string;
  h?: number;
  r?: number;
  className?: string;
}) {
  return (
    <div
      className={`animate-pulse bg-[#EDE8DC] ${className}`}
      style={{ width: w ?? "100%", height: h, borderRadius: r }}
    />
  );
}

// full-width empty state used when a selection returns no data
export function EmptyState({
  title,
  message,
  actionLabel,
  onAction,
}: {
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <Card className="flex flex-col items-center justify-center gap-3.5 p-12 text-center">
      <div className="flex h-16 w-16 items-end justify-center gap-1 rounded-full bg-[#28543D]/5 p-[18px]">
        {[14, 22, 12].map((barH, i) => (
          <span
            key={i}
            className="w-1.5 rounded-[3px] bg-[#6E9A7B]"
            style={{ height: barH }}
          />
        ))}
      </div>
      <div className="text-[17px] font-bold text-[#294B39]">{title}</div>
      <div className="max-w-[380px] text-[13.5px] text-[#687368]">
        {message}
      </div>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="mt-1 cursor-pointer rounded-[6px] border-none bg-[#28543D] hover:bg-[#204833] px-5 py-2.5 text-[13.5px] font-bold text-white"
        >
          {actionLabel}
        </button>
      )}
    </Card>
  );
}

// compact in-card state for metrics needing a minimum sample to report
export function NotEnoughData({
  message,
  minHeight = 160,
}: {
  message?: string;
  minHeight?: number;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2.5 p-5 text-center"
      style={{ minHeight }}
    >
      <div className="flex h-11 w-11 items-end justify-center gap-[3px] rounded-full bg-[#F3EDE2] p-3">
        {[8, 14, 6].map((barH, i) => (
          <span
            key={i}
            className="w-[5px] rounded-[3px] bg-black/20"
            style={{ height: barH }}
          />
        ))}
      </div>
      <div className="text-sm font-bold text-[#294B39]">Not enough data</div>
      <div className="max-w-[300px] text-[12.5px] text-[#687368]">
        {message ??
          "Too few students in this selection to report reliably. Widen the filters to see this metric."}
      </div>
    </div>
  );
}
